/**
 * Odkaz z Tandem Zostáv do appky z Chromu (KOLO 215).
 *
 * User má Rozpočet nainštalovaný z Chromu ako appku: *„keď idem
 * z TopSolidu posielať export, tak mi otvoríš vždy nový web v prehliadači,
 * nie samostatnú appku, a keď aj mám v prehliadači otvorené okno Rozpočtu,
 * tak vytvoríš nové"*. Program Tandem Zostavy otvára `url` z odpovede
 * (`/zakazky?koncept=<id>`) v predvolenom prehliadači.
 *
 * - Manifest nesie `launch_handler: focus-existing` — keď odkaz dostane
 *   appka, otvorené okno ho prevezme cez `window.launchQueue` bez
 *   prenačítania (`cielOdkazu`).
 * - Karta prehliadača, ktorá vie, že appka je nainštalovaná (`appkaNainstalovana`),
 *   ho odovzdá appke protokolom `web+rozpocet:` (manifest `protocol_handlers`)
 *   a zavrie sa, keď appka potvrdí prevzatie (`KANAL`).
 *
 * Úložisko môže chýbať (súkromné okno) — vtedy sa nič neodovzdáva a odkaz
 * sa otvorí v karte ako doteraz.
 */

export const PROTOKOL = "web+rozpocet";
/** Appka potvrdí prevzatie konceptu karte, ktorá ho odovzdala. */
export const KANAL = "tandem-rozpocet-appka";

const KLUC_APPKY = "tandem.appka";
/** Karta, kde user zvolil „Pokračovať tu" — už sa neodovzdáva. */
const KLUC_TU = "tandem.appka.tu";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type CielOdkazu = { koncept: string } | { cesta: string };

/** Prechod karty na odkaz a jej zavretie — v testoch sa vymenia. */
export const prehliadac = {
  prejdi(url: string): void {
    window.location.href = url;
  },
  zavri(): void {
    window.close();
  },
};

/** `web+rozpocet:koncept/<id>` — odkaz, ktorým karta otvorí appku. */
export function odkazDoAppky(konceptId: string): string {
  return `${PROTOKOL}:koncept/${konceptId}`;
}

/** Koncept z `web+rozpocet:koncept/<id>` (aj s `//`), inak null. */
export function konceptZProtokolu(odkaz: string | null): string | null {
  if (!odkaz) return null;
  const m = /^web\+rozpocet:(?:\/\/)?koncept\/([^/?#]+)\/?$/i.exec(odkaz.trim());
  const id = m?.[1];
  return id && UUID.test(id) ? id : null;
}

/** Koncept z `?koncept=` zoznamu zákaziek, inak null. */
export function konceptZAdresy(pathname: string, search: string): string | null {
  if (pathname !== "/zakazky") return null;
  const id = new URLSearchParams(search).get("koncept");
  return id && UUID.test(id) ? id : null;
}

/**
 * Kam appka pôjde s adresou, ktorú dostala pri spustení. Koncept sa otvorí
 * oknom nad stránkou (rozpísaná zákazka ostane), iná adresa je cesta.
 * Cudzí pôvod = null — appka nikam nejde.
 */
export function cielOdkazu(url: string, povod: string): CielOdkazu | null {
  let u: URL;
  try {
    u = new URL(url, povod);
  } catch {
    return null;
  }
  if (u.origin !== povod) return null;
  if (u.pathname === "/otvor") {
    const koncept = konceptZProtokolu(u.searchParams.get("odkaz"));
    return koncept ? { koncept } : { cesta: "/zakazky" };
  }
  const koncept = konceptZAdresy(u.pathname, u.search);
  if (koncept) return { koncept };
  return { cesta: `${u.pathname}${u.search}${u.hash}` };
}

/** Beží Rozpočet v okne appky (nie v karte prehliadača)? */
export function jeAppka(): boolean {
  if (typeof window.matchMedia !== "function") return false;
  return ["standalone", "window-controls-overlay", "minimal-ui"].some(
    (rezim) => window.matchMedia(`(display-mode: ${rezim})`).matches,
  );
}

/** Okno appky si poznačí, že appka v tomto profile Chromu je. */
export function zapamatajAppku(): void {
  try {
    window.localStorage.setItem(KLUC_APPKY, new Date().toISOString());
  } catch {
    // bez úložiska sa odkaz z karty len neodovzdá
  }
}

export function zabudniAppku(): void {
  try {
    window.localStorage.removeItem(KLUC_APPKY);
  } catch {
    // nič
  }
}

export function appkaNainstalovana(): boolean {
  try {
    return window.localStorage.getItem(KLUC_APPKY) !== null;
  } catch {
    return false;
  }
}

/** Táto karta ostáva pri odkazoch v prehliadači (user zvolil „Pokračovať tu"). */
export function ostanTu(): void {
  try {
    window.sessionStorage.setItem(KLUC_TU, "1");
  } catch {
    // nič
  }
}

export function kartaOstavaTu(): boolean {
  try {
    return window.sessionStorage.getItem(KLUC_TU) !== null;
  } catch {
    return true;
  }
}

/** Má karta odkaz odovzdať appke? */
export function odovzdatAppke(pathname: string, search: string): string | null {
  const koncept = konceptZAdresy(pathname, search);
  if (!koncept || jeAppka() || kartaOstavaTu() || !appkaNainstalovana()) return null;
  return koncept;
}
